import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { AuthContext } from "@/hooks/use-auth";
import { queryClient } from "@/lib/query-client";
import { bootstrapSession, setSessionLostHandler } from "@/services/api-client";
import * as authService from "@/services/auth.service";
import type { User } from "@/types/api";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // A refused refresh (expired, or revoked by a password change elsewhere)
  // drops the user, so ProtectedRoute shows the login screen at once.
  useEffect(() => {
    setSessionLostHandler(() => {
      setUser(null);
      queryClient.clear();
    });
    return () => setSessionLostHandler(null);
  }, []);

  // On load there is no access token in memory (by design — see api-client), so
  // trade the HttpOnly refresh cookie for one before rendering any route.
  // Without this, every reload would bounce a signed-in user to /login.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (await bootstrapSession()) {
          const currentUser = await authService.getCurrentUser();
          if (!cancelled) setUser(currentUser);
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    setUser(await authService.login({ username, password }));
  }, []);

  const signOut = useCallback(async () => {
    await authService.logout();
    setUser(null);
    // Drop every cached response, so the next person to use this browser cannot
    // read the previous user's data out of the query cache.
    queryClient.clear();
  }, []);

  const refreshUser = useCallback(async () => {
    setUser(await authService.getCurrentUser());
  }, []);

  const value = useMemo(
    () => ({ user, isLoading, signIn, signOut, refreshUser }),
    [user, isLoading, signIn, signOut, refreshUser],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
